import { useListFilters, useListPagination } from "../../hooks/useListState.js";
import { useState } from "react";
import { Alert, App, Button, Input, Table } from "antd";
import { Plus, Search } from "lucide-react";
import { isManager, rpc } from "../../lib/supabase.js";
import { SchoolCreateModal } from "./SchoolCreateModal.jsx";
import { AppEmpty, StatusTag } from "../../components/Feedback.jsx";
import { schoolDisplayName } from "../../utils/schoolDisplay.js";

export function SchoolSettings({ store }) {
  const { modal } = App.useApp();
  const [filters, setFilters] = useListFilters("list:schools:filter", { search: "" });
  const search = filters.search;
  const setSearch = search => setFilters({ search });
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [manual, setManual] = useState(false);
  const manager = isManager(store.context.role);
  const schools = store.state.academyInfo.schools || [];
  const filteredSchools = schools.filter((school) => `${school.name} ${school.city} ${school.district}`.includes(search.trim()));
  const pagination = useListPagination("list:schools:page", filteredSchools.length, search, store.loading || !!store.error);
  async function save(school, active = true) {
    if (busy || !manager) return;
    setBusy(true); setError("");
    try {
      await rpc("save_academy_school", {
        input_catalog_id: school.catalogId || null, input_name: school.name,
        input_school_level: school.schoolLevel, input_city: school.city,
        input_district: school.district, input_active: active,
      });
      await store.refreshSchools();
    } catch (failure) { setError(failure.message); }
    finally { setBusy(false); }
  }
  return <div className="academy-tab-panel school-management-panel">
    {error && <Alert type="error" showIcon message={error} />}
    <div className="section-toolbar school-management-toolbar">
      <Input className="school-management-search" prefix={<Search size={15} />} allowClear aria-label="학교 검색" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="학교 검색" />
      {manager && <Button className="section-toolbar-primary" type="primary" icon={<Plus size={16} />} disabled={busy} onClick={() => setManual(true)}>학교 추가</Button>}
    </div>
    <p className="school-management-hint">위 검색창은 이미 추가한 학교만 검색합니다. 학교를 비활성화해도 기존 학생의 학교 기록은 유지됩니다.</p>
    <Table className="management-list-table school-management-table" rowKey={(row) => row.catalogId || row.choiceId} dataSource={filteredSchools} scroll={{ x: 640 }} pagination={pagination} locale={{ emptyText: <AppEmpty description={search.trim() ? "추가한 학교 중 검색 결과가 없습니다. 새 학교는 ‘학교 추가’에서 검색해 주세요." : "추가한 학교가 없습니다. 학교 추가에서 검색해 주세요."} /> }} columns={[
      { title: "학교", dataIndex: "name", render: name => schoolDisplayName(name, store.state.academyInfo) }, { title: "과정", dataIndex: "schoolLevel" },
      { title: "지역", render: (_, row) => `${row.city} ${row.district}` },
      { title: "출처", render: (_, row) => row.source === "official" ? "나이스" : "관리자 입력" },
      { title: "상태", width: 90, render: (_, row) => <StatusTag tone={row.active ? "positive" : "neutral"}>{row.active ? "사용" : "비활성"}</StatusTag> },
      ...(manager ? [{ title: "관리", width: 100, fixed: "right", align: "center", render: (_, row) => <Button className="school-row-action" size="small" disabled={busy} onClick={() => modal.confirm({
        title: row.active ? "새 학생 선택 목록에서 제외할까요?" : "학교를 다시 사용할까요?",
        content: "기존 학생의 학교명은 변경되지 않습니다.",
        okText: row.active ? "비활성화" : "재사용",
        cancelText: "취소",
        onOk: () => save(row, !row.active),
      })}>{row.active ? "비활성화" : "사용"}</Button> }] : []),
    ]} />
    {manager && <SchoolCreateModal open={manual} onCancel={() => setManual(false)} onSaved={async () => {
      await store.refreshSchools(); setManual(false);
    }} />}
  </div>;
}
